import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getTodosFichajes, getTrabajadores } from '../../../services/apis';
import { SubHeader } from '../../../utils/componentes-utiles/SubHeader';
import AutocompleteList from '../../../utils/componentes-utiles/AutocompleteList';
import { mesName } from '../../../utils/utils';
import { BarrasHorasInvertidasHorizontalesDia } from '../../../utils/componentes-utiles/graficas/BarrasHorasInvertidasHorizontalesDia';

export const Jornadas = () => {
    const [fichajes, setFichajes] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [yearSelected, setYearSelected] = useState(new Date().getFullYear());
    const [nombreMes, setNombreMes] = useState(mesName(mes));
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [opcionesUsuarios, setOpcionesUsuarios] = useState([]);
    const [loadedFilters, setLoadedFilters] = useState(false);
    const [filtros, setFiltros] = useState({ fechaDesde: '', fechaHasta: '' });

    useEffect(() => {
        const fechaDesde = `${yearSelected}-${String(mes).padStart(2, '0')}-01`;
        const lastDay = new Date(yearSelected, mes, 0).getDate();
        const fechaHasta = `${yearSelected}-${String(mes).padStart(2, '0')}-${lastDay}`;

        setFiltros({ fechaDesde, fechaHasta });
        setNombreMes(mesName(mes));
        setLoadedFilters(false);
    }, [mes, yearSelected]);

    useEffect(() => {
        if (filtros.fechaDesde && filtros.fechaHasta) {
            consultarFichaje();
        }
    }, [filtros, selectedUsers]);

    useEffect(() => {
        cargarTrabajadores();
    }, []);

    const consultarFichaje = async () => {
        try {
            setLoadedFilters(false);
            const usuarioIds = selectedUsers.map(usuario => usuario.value);
            const response = await getTodosFichajes(filtros.fechaDesde, filtros.fechaHasta, usuarioIds);

            setFichajes(response?.data?.fichajes || []);
            setLoaded(true);
            setLoadedFilters(true);
        } catch (error) {
            console.error('Error al consultar los fichajes:', error);
        }
    };

    const cargarTrabajadores = async () => {
        try {
            const response = await getTrabajadores();
            const opciones = response.data.map(item => ({
                label: item.nombre,
                value: item.id
            }));
            setOpcionesUsuarios(opciones);
        } catch (error) {
            console.error('Error al cargar trabajadores', error);
        }
    };

    const obtenerFechaCompleta = (diaTexto) => {
        const match = diaTexto.match(/[A-Z](\d{1,2})\/(\d{1,2})/);
        if (!match) return diaTexto;

        const dia = parseInt(match[1], 10);
        const numeroMes = parseInt(match[2], 10) - 1;
        const fecha = new Date(yearSelected, numeroMes, dia);
        const diasLargos = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const mesesLargos = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

        return `${diasLargos[fecha.getDay()]} ${fecha.getDate()} de ${mesesLargos[fecha.getMonth()]}`;
    };

    const convertirHoras = (horas) => {
        const h = Math.floor(horas);
        const m = Math.round((horas - h) * 60);
        return `${h}h${m > 0 ? ` ${m}min` : ''}`;
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <SubHeader />
            <Box
                className="fade-in-2"
                sx={{
                    width: '100%',
                    px: { xs: 1.5, sm: 2.5, md: 4 },
                    py: { xs: 2, md: 4 },
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                }}
            >
                {!loaded && <CircularProgress />}
                {loaded && (
                    <Grid container direction="row" alignItems="center" justifyContent="center">
                        <Grid container direction="row" alignItems="center" justifyContent="center" xs={11.5} md={12}>
                            <Grid container direction="row" marginBottom="0.5em" alignItems="center" justifyContent="center" xs={12} gap={1}>
                                <Grid container xs={12} md={2} direction="row" justifyContent="start" alignItems="start">
                                    <h3>Fichajes de {nombreMes}</h3>&nbsp;
                                    {!loadedFilters && <CircularProgress size={22} />}
                                </Grid>
                                <Grid container xs={6} md={2} gap={1} justifyContent="center" alignItems="center">
                                    <TextField
                                        label="Desde"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={filtros.fechaDesde}
                                        onChange={(event) => setFiltros(prev => ({ ...prev, fechaDesde: event.target.value }))}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid container xs={6} md={2} gap={1} justifyContent="center" alignItems="center">
                                    <TextField
                                        label="Hasta"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={filtros.fechaHasta}
                                        onChange={(event) => setFiltros(prev => ({ ...prev, fechaHasta: event.target.value }))}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid container xs={12} md={3} justifyContent="center" alignItems="center">
                                    <AutocompleteList
                                        array={opcionesUsuarios}
                                        setArray={setSelectedUsers}
                                        arrayPre={selectedUsers}
                                        label="Selecciona uno o más usuarios"
                                    />
                                </Grid>
                            </Grid>

                            {fichajes.length === 0 && (
                                <Grid container xs={12} justifyContent="center" alignItems="center">
                                    <strong>Todavía no hay fichajes</strong>
                                </Grid>
                            )}

                            {fichajes.map((usuario, indexUsuario) => (
                                <Accordion key={usuario.id || indexUsuario} sx={{ width: '100%', my: 1 }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`usuario-${indexUsuario}-content`}
                                        id={`usuario-${indexUsuario}-header`}
                                    >
                                        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                            <Typography fontWeight={600}>{usuario.usuario}</Typography>
                                            <Typography fontSize={14} color="textSecondary">
                                                Total: {convertirHoras(usuario.horasSemana?.reduce((sum, dia) =>
                                                    sum + dia.tareas.reduce((suma, tarea) => suma + tarea.horas, 0), 0) || 0)}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {usuario?.horasSemana?.length > 0 ? (
                                            usuario.horasSemana.map((dia, indexDia) => {
                                                const totalDia = dia.tareas.reduce((suma, tarea) => suma + tarea.horas, 0);
                                                return dia.tareas.length > 0 && (
                                                    <Accordion key={indexDia} sx={{ mb: 1.5 }}>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            aria-controls={`panel-${indexUsuario}-${indexDia}-content`}
                                                            id={`panel-${indexUsuario}-${indexDia}-header`}
                                                        >
                                                            <Box>
                                                                <Typography variant="subtitle1">
                                                                    {obtenerFechaCompleta(dia.dia)}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Total: {convertirHoras(totalDia)}
                                                                </Typography>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Box sx={{ overflowX: 'auto' }}>
                                                                <BarrasHorasInvertidasHorizontalesDia data={dia.tareas} />
                                                            </Box>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                No hay tareas registradas este mes.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Box>
    );
};
